import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import apiUtils from '../../global/utils/api';

// import `UserProfile` stuff that we need to use in the auth store
import { sendCreateUserProfile } from '../userProfile/userProfileStore';

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.

export const sendVerificationEmail = createAsyncThunk(
  'auth/sendVerificationEmail'
  , async ({ email, otherParams = {} }) => {
    const response = await apiUtils.callAPI('/api/users/send-verification-email', 'POST', { email, otherParams });
    return response;
  }
);

export const sendCheckVerificationToken = createAsyncThunk(
  'auth/sendCheckVerificationToken'
  , async (token) => {
    // Adding noreferrer to the headers on this call to avoid referrer leakage: https://portswigger.net/kb/issues/00500400_cross-domain-referer-leakage
    const response = await apiUtils.callAPI('/api/users/check-verification-token', 'POST', { token }, {
    'Accept': 'application/json', 'Content-Type': 'application/json', 'Referrer-Policy': 'no-referrer'
  });
    return response
  }
);

export const sendRegister = createAsyncThunk(
  'auth/sendRegister'
  , async (userInfo, { rejectWithValue }) => {
    try {
      return await apiUtils.callAPI('/api/users/register', 'POST', userInfo);
    } catch(error) {
      return rejectWithValue(typeof error === 'object' && error !== null ? error : { message: error });
    }
  }
);

export const sendLogin = createAsyncThunk(
  'auth/sendLogin'
  , async (userInfo) => {
    const response = await apiUtils.callAPI('/api/users/login', 'POST', userInfo);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

export const sendLogout = createAsyncThunk(
  'auth/sendLogout'
  , async () => {
    const response = await apiUtils.callAPI('/api/users/logout', 'POST');
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

export const sendGetLoggedIn = createAsyncThunk(
  'auth/sendGetLoggedIn'
  , async () => {
    const response = await apiUtils.callAPI('/api/users/get-logged-in', 'POST');
    return response
  }
);

export const sendRequestPasswordReset = createAsyncThunk(
  'auth/sendRequestPasswordReset'
  , async ({email, redirect, loginLink}) => {
    const response = await apiUtils.callAPI('/api/users/request-password-reset', 'POST', { email, redirect, loginLink });
    return response
  }
);

export const sendCheckResetToken = createAsyncThunk(
  'auth/sendCheckResetToken'
  , async (hex) => {
    // Adding noreferrer to the headers on this call to avoid referrer leakage: https://portswigger.net/kb/issues/00500400_cross-domain-referer-leakage
    const response = await apiUtils.callAPI('/api/users/check-reset-token', 'POST', { hex }, {
    'Accept': 'application/json', 'Content-Type': 'application/json', 'Referrer-Policy': 'no-referrer'
  });
    return response
  }
);

export const sendResetPassword = createAsyncThunk(
  'auth/sendResetPassword'
  , async ({hex, password}) => {
    // Adding noreferrer to the headers on this call to avoid referrer leakage: https://portswigger.net/kb/issues/00500400_cross-domain-referer-leakage
    const response = await apiUtils.callAPI('/api/users/reset-password', 'POST', { hex, password }, {
    'Accept': 'application/json', 'Content-Type': 'application/json', 'Referrer-Policy': 'no-referrer'
  });
    return response
  }
);

export const sendUpdatePassword = createAsyncThunk(
  'auth/sendUpdatePassword'
  , async ({ oldPass, newPass, confirmPass }) => {
    const response = await apiUtils.callAPI('/api/users/password', 'POST', { oldPass, newPass, confirmPass });
    return response
  }
);

export const sendAgreeToTerms = createAsyncThunk(
  'auth/sendAgreeToTerms'
  , async () => {
    const response = await apiUtils.callAPI('/api/users/agree-to-terms', 'POST');
    return response
  }
);

export const sendCheckOnboardingToken = createAsyncThunk(
  'auth/sendCheckOnboardingToken'
  , async (hex) => {
    // Adding noreferrer to the headers on this call to avoid referrer leakage: https://portswigger.net/kb/issues/00500400_cross-domain-referer-leakage
    const response = await apiUtils.callAPI('/api/users/check-onboarding-token', 'POST', { hex }, {
    'Accept': 'application/json', 'Content-Type': 'application/json', 'Referrer-Policy': 'no-referrer'
  });
    return response
  }
);

export const sendOnboarding = createAsyncThunk(
  'auth/sendOnboarding'
  , async ({hex, password, ...rest}) => {
    const response = await apiUtils.callAPI('/api/users/onboarding', 'POST', { hex, password, ...rest });
    return response
  }
);

export const requestNewOnboardingLink = createAsyncThunk(
  'auth/requestNewOnboardingLink'
  , async ({ hex, email }) => {
    const response = await apiUtils.callAPI('/api/users/request-new-onboarding-link', 'POST', { hex, email });
    return response
  }
);

export const sendGetSSOLinks = createAsyncThunk(
  'auth/sendGetSSOLinks'
  , async (email) => {
    const response = await apiUtils.callAPI('/api/users/get-sso-links', 'POST', { email });
    return response
  }
);

export const sendGetLoginOptions = createAsyncThunk(
  'auth/sendGetLoginOptions'
  , async (email) => {
    const response = await apiUtils.callAPI('/api/users/get-login-options', 'POST', { email });
    return response
  }
);

export const authStore = createSlice({
  name: 'auth'
  , initialState: {
    loggedInUser: null
    , status: 'idle'
    , error: null
  }
  // The `reducers` field lets us define reducers and generate associated actions
  , reducers: {
    // none needed here
  }
  // The `extraReducers` field lets the store handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other stores.
  , extraReducers: (builder) => {
    builder
      .addCase(sendRegister.pending, (state) => {
        state.status = 'pending';
        state.error = null
      })
      .addCase(sendRegister.fulfilled, (state, action) => {
        state.status = 'idle';
        state.loggedInUser = action.payload;
      })
      .addCase(sendRegister.rejected, (state, action) => {
        state.status = 'rejected'
        state.error = action.error.message
      })
      .addCase(sendLogin.pending, (state) => {
        state.status = 'pending';
        state.error = null
      })
      .addCase(sendLogin.fulfilled, (state, action) => {
        state.status = 'idle';
        state.loggedInUser = action.payload;
      })
      .addCase(sendLogin.rejected, (state, action) => {
        state.status = 'rejected';
        state.error = action.error.message;
      })
      .addCase(sendLogout.pending, (state) => {
        state.status = 'pending';
        state.error = null;
      })
      .addCase(sendLogout.fulfilled, (state) => {
        state.status = 'idle';
        state.loggedInUser = null;
      })
      .addCase(sendLogout.rejected, (state, action) => {
        state.status = 'rejected';
        state.error = action.error.message;
      })
      .addCase(sendGetLoggedIn.pending, (state) => {
        state.status = 'pending';
        state.error = null;
      })
      .addCase(sendGetLoggedIn.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.loggedInUser = action.payload;
      })
      .addCase(sendGetLoggedIn.rejected, (state, action) => {
        state.status = 'rejected';
        state.error = action.error.message;
      })
      .addCase(sendRequestPasswordReset.pending, (state) => {
        state.status = 'pending';
        state.error = null;
      })
      .addCase(sendRequestPasswordReset.fulfilled, (state, action) => {
        state.status = 'idle';
        state.loggedInUser = null;
      })
      .addCase(sendRequestPasswordReset.rejected, (state, action) => {
        state.status = 'rejected';
        state.error = action.error.message;
      })
      .addCase(sendResetPassword.pending, (state) => {
        state.status = 'pending';
        state.error = null;
      })
      .addCase(sendResetPassword.fulfilled, (state, action) => {
        state.status = 'idle';
        state.loggedInUser = null;
      })
      .addCase(sendResetPassword.rejected, (state, action) => {
        state.status = 'rejected';
        state.error = action.error.message;
      })
      .addCase(sendUpdatePassword.pending, (state) => {
        // do nothing? this should probably live in a separate store along with the reset password stuff since it doesn't deal with loggedInUser state.
      })
      .addCase(sendUpdatePassword.fulfilled, (state, action) => {
        // do nothing? this should probably live in a separate store along with the reset password stuff since it doesn't deal with loggedInUser state.
      })
      .addCase(sendUpdatePassword.rejected, (state, action) => {
        // do nothing? this should probably live in a separate store along with the reset password stuff since it doesn't deal with loggedInUser state.
      })

      // AGREE TO TERMS
      // update the loggedInUser object with the returned user object from the API
      .addCase(sendAgreeToTerms.fulfilled, (state, action) => {
        state.status = 'idle';
        state.loggedInUser = action.payload
      })
      .addCase(sendAgreeToTerms.rejected, (state, action) => {
        state.status = 'rejected';
        state.error = action.error.message;
      })
      // USER ONBOARDING
      // update the loggedInUser object with the returned user object from the API
      .addCase(sendOnboarding.fulfilled, (state, action) => {
        state.status = 'idle';
        state.loggedInUser = action.payload
      })
      .addCase(sendOnboarding.rejected, (state, action) => {
        state.status = 'rejected';
        state.error = action.error.message;
      })
      // USER PROFILE
      // on user profile creation, we need to set the _profile field on the user object, this is easier than trying to invalidate the auth session with redirects flying around
      .addCase(sendCreateUserProfile.fulfilled, (state, action) => {
        state.loggedInUser = { ...state.loggedInUser, _profile: action.payload._id };
      })
  }
});





// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
/**
 * 
 * @returns logged in user object
 */
export const selectLoggedInUser = ({ auth }) => {
  return auth.loggedInUser;
}

/**
 * 
 * @returns logged in user fetch status
 */
export const selectAuthStatus = ({ auth }) => {
  return auth.status;
}

/**
 * @returns available sso providers for admin to set
 */
export const selectSSOProviders = ({ auth }) => {
  return auth.ssoProviders;
}

export default authStore.reducer;

